import { useCallback, useState } from 'react';

const KEY_BASE = {
  fontFamily: "'Press Start 2P', monospace",
  fontSize: 13,
  background: '#0a0a0a',
  color: '#34ff77',
  border: '1px solid #2a2a2a',
  padding: '14px 0',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  touchAction: 'manipulation',
  textAlign: 'center',
  lineHeight: 1,
};

const KEY_TEXT = { ...KEY_BASE, fontSize: 11, padding: '12px 0' };

const VARIANTS = {
  default: {},
  submit: { background: '#34ff77', color: '#000', borderColor: '#34ff77' },
  accent: { color: '#ffbd2e', borderColor: '#3a2a0a' },
  erase: { color: '#ff5f56', borderColor: '#3a1010' },
  meridiem: { color: '#34ff77', borderColor: '#1f3a26' },
  toggle: { color: '#7ae0ff', borderColor: '#1a2a3a' },
  toggleActive: { background: '#7ae0ff', color: '#000', borderColor: '#7ae0ff' },
};

function Key({ label, onPress, variant = 'default', span = 1, ariaLabel, disabled, base = KEY_BASE }) {
  const fire = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    onPress();
  };
  const style = {
    ...base,
    ...VARIANTS[variant],
    gridColumn: `span ${span}`,
    opacity: disabled ? 0.3 : 1,
  };
  return (
    <button
      type="button"
      style={style}
      onPointerDown={fire}
      aria-label={ariaLabel || label}
    >
      {label}
    </button>
  );
}

export default function TouchKeypad({ mode, value, onChange, onSubmit }) {
  const append = useCallback((c) => onChange((value || '') + c), [value, onChange]);
  const erase = useCallback(() => onChange((value || '').slice(0, -1)), [value, onChange]);
  const clear = useCallback(() => onChange(''), [onChange]);

  const setMeridiem = useCallback((mer) => {
    const stripped = (value || '').replace(/\s*(am|pm)\s*$/i, '').trimEnd();
    onChange(stripped + ' ' + mer);
  }, [value, onChange]);

  // text-mode local state (shift + symbols layer)
  const [shift, setShift] = useState(false);
  const [symbols, setSymbols] = useState(false);

  if (mode !== 'date' && mode !== 'time' && mode !== 'text') return null;

  const grid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 4,
    padding: 8,
    background: '#050508',
    borderTop: '1px solid #1a1a1a',
    flexShrink: 0,
  };

  if (mode === 'date') {
    return (
      <div style={grid} role="group" aria-label="Date keypad">
        <Key label="1" onPress={() => append('1')} />
        <Key label="2" onPress={() => append('2')} />
        <Key label="3" onPress={() => append('3')} />
        <Key label="/" variant="accent" onPress={() => append('/')} ariaLabel="Slash" />
        <Key label="4" onPress={() => append('4')} />
        <Key label="5" onPress={() => append('5')} />
        <Key label="6" onPress={() => append('6')} />
        <Key label="⌫" variant="erase" onPress={erase} ariaLabel="Backspace" />
        <Key label="7" onPress={() => append('7')} />
        <Key label="8" onPress={() => append('8')} />
        <Key label="9" onPress={() => append('9')} />
        <Key label="CLR" variant="erase" onPress={clear} ariaLabel="Clear" />
        <Key label="0" span={3} onPress={() => append('0')} />
        <Key label="ENTER" variant="submit" onPress={onSubmit} />
      </div>
    );
  }

  if (mode === 'time') {
    const cur = (value || '').match(/(am|pm)\s*$/i);
    const activeMer = cur ? cur[1].toLowerCase() : null;
    const merStyle = (mer) => ({
      background: activeMer === mer ? '#34ff77' : '#0a0a0a',
      color: activeMer === mer ? '#000' : '#34ff77',
      borderColor: activeMer === mer ? '#34ff77' : '#1f3a26',
    });

    return (
      <div style={grid} role="group" aria-label="Time keypad">
        <Key label="1" onPress={() => append('1')} />
        <Key label="2" onPress={() => append('2')} />
        <Key label="3" onPress={() => append('3')} />
        <Key label=":" variant="accent" onPress={() => append(':')} ariaLabel="Colon" />
        <Key label="4" onPress={() => append('4')} />
        <Key label="5" onPress={() => append('5')} />
        <Key label="6" onPress={() => append('6')} />
        <Key label="⌫" variant="erase" onPress={erase} ariaLabel="Backspace" />
        <Key label="7" onPress={() => append('7')} />
        <Key label="8" onPress={() => append('8')} />
        <Key label="9" onPress={() => append('9')} />
        <Key label="CLR" variant="erase" onPress={clear} ariaLabel="Clear" />
        <Key label="0" span={2} onPress={() => append('0')} />
        <button
          type="button"
          style={{ ...KEY_BASE, ...merStyle('am'), gridColumn: 'span 1' }}
          onPointerDown={(e) => { e.preventDefault(); setMeridiem('AM'); }}
          aria-label="AM"
        >AM</button>
        <button
          type="button"
          style={{ ...KEY_BASE, ...merStyle('pm'), gridColumn: 'span 1' }}
          onPointerDown={(e) => { e.preventDefault(); setMeridiem('PM'); }}
          aria-label="PM"
        >PM</button>
        <Key label="ENTER" variant="submit" span={4} onPress={onSubmit} />
      </div>
    );
  }

  // mode === 'text' — QWERTY with shift + symbols layer
  // 10-column grid for tighter rows
  const textGrid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 1fr)',
    gap: 3,
    padding: 6,
    background: '#050508',
    borderTop: '1px solid #1a1a1a',
    flexShrink: 0,
  };

  const tap = (c) => {
    append(c);
    if (shift) setShift(false);
  };

  if (symbols) {
    const syms = ['1','2','3','4','5','6','7','8','9','0'];
    const punct = ['!','?','@','#','$','%','&','*','(',')'];
    const more = ['-','_','+','=','/',';',':','"','\'','`'];
    return (
      <div style={textGrid} role="group" aria-label="Symbols keypad">
        {syms.map((c) => <Key key={'s'+c} label={c} onPress={() => tap(c)} base={KEY_TEXT} />)}
        {punct.map((c) => <Key key={'p'+c} label={c} onPress={() => tap(c)} base={KEY_TEXT} />)}
        {more.map((c) => <Key key={'m'+c} label={c} onPress={() => tap(c)} base={KEY_TEXT} />)}
        <Key label="ABC" variant="toggle" span={2} onPress={() => setSymbols(false)} base={KEY_TEXT} />
        <Key label="space" span={4} onPress={() => append(' ')} base={KEY_TEXT} />
        <Key label="." onPress={() => append('.')} base={KEY_TEXT} />
        <Key label="," onPress={() => append(',')} base={KEY_TEXT} />
        <Key label="⌫" variant="erase" onPress={erase} ariaLabel="Backspace" base={KEY_TEXT} />
        <Key label="ENTER" variant="submit" span={10} onPress={onSubmit} base={KEY_TEXT} />
      </div>
    );
  }

  const xform = (c) => shift ? c.toUpperCase() : c;
  const row1 = ['q','w','e','r','t','y','u','i','o','p'];
  const row2 = ['a','s','d','f','g','h','j','k','l'];
  const row3 = ['z','x','c','v','b','n','m'];

  return (
    <div style={textGrid} role="group" aria-label="QWERTY keypad">
      {row1.map((c) => <Key key={'r1'+c} label={xform(c)} onPress={() => tap(xform(c))} base={KEY_TEXT} />)}
      <Key label="" base={{ ...KEY_TEXT, background: 'transparent', border: 'none', cursor: 'default' }} onPress={() => {}} />
      {row2.map((c) => <Key key={'r2'+c} label={xform(c)} onPress={() => tap(xform(c))} base={KEY_TEXT} />)}
      <Key label="" base={{ ...KEY_TEXT, background: 'transparent', border: 'none', cursor: 'default' }} onPress={() => {}} />
      <Key label={shift ? '⇧' : '⇧'} variant={shift ? 'toggleActive' : 'toggle'} onPress={() => setShift(!shift)} base={KEY_TEXT} ariaLabel="Shift" />
      {row3.map((c) => <Key key={'r3'+c} label={xform(c)} onPress={() => tap(xform(c))} base={KEY_TEXT} />)}
      <Key label="⌫" variant="erase" span={2} onPress={erase} ariaLabel="Backspace" base={KEY_TEXT} />
      <Key label="123" variant="toggle" span={2} onPress={() => setSymbols(true)} base={KEY_TEXT} />
      <Key label="space" span={4} onPress={() => append(' ')} base={KEY_TEXT} />
      <Key label="." onPress={() => append('.')} base={KEY_TEXT} />
      <Key label="," onPress={() => append(',')} base={KEY_TEXT} />
      <Key label="ENTER" variant="submit" span={2} onPress={onSubmit} base={KEY_TEXT} />
    </div>
  );
}
